/**
 * Resolve tenant / white-label branding from the browser hostname.
 * Bank buyer apps on *.banks.vaiu.app proxy to bank platform APIs on *.api.vaiu.io.
 */

export type TenantBrandKey =
  | 'vaiu'
  | 'ing'
  | 'bnp'
  | 'rbko'
  | 'payformyfood'
  | 'atomic'
  | 'vaiupay';

export interface TenantBranding {
  brandKey: TenantBrandKey;
  displayName: string;
  /** Primary logo path under public/ (PNG/SVG deployed per tenant). */
  logoSrc: string;
  /** Bank/partner skins show subtle Vaiu attribution when logo is bank-only. */
  showPoweredByVaiu: boolean;
  /** Tailwind classes for the decorative top accent strip. */
  accentBarClass: string;
}

/** Vaiu | Tokenized Pay — core consumer app mark (public/img/logo.png). */
export const VAIU_LOGO_SRC = '/img/logo.png';

/** Co-branded bank header logos live under public/img/banks/{bank}-logo.png */
export function bankLogoSrc(bankKey: Exclude<TenantBrandKey, 'vaiu' | 'atomic' | 'vaiupay'>): string {
  return `/img/banks/${bankKey}-logo.png`;
}

const BANK_SUBDOMAINS: TenantBrandKey[] = ['ing', 'bnp', 'rbko', 'payformyfood'];

export function isBankTenantBrand(brandKey: TenantBrandKey): boolean {
  return BANK_SUBDOMAINS.includes(brandKey);
}

export function resolveTenantBranding(
  hostname: string = typeof window !== 'undefined' ? window.location.hostname : ''
): TenantBranding {
  const host = hostname.toLowerCase();

  if (host.includes('ing.banks.vaiu.app')) {
    return {
      brandKey: 'ing',
      displayName: 'ING',
      logoSrc: bankLogoSrc('ing'),
      showPoweredByVaiu: false,
      accentBarClass: 'bg-[#FF6200]',
    };
  }

  if (host.includes('bnp.banks.vaiu.app')) {
    return {
      brandKey: 'bnp',
      displayName: 'BNP Paribas',
      logoSrc: bankLogoSrc('bnp'),
      showPoweredByVaiu: false,
      accentBarClass: 'bg-[#009A44]',
    };
  }

  if (host.includes('rbko.')) {
    return {
      brandKey: 'rbko',
      displayName: 'Raiffeisen Bank Kosovo',
      logoSrc: '/img/brands/RBKo/RBKo_logo.png',
      showPoweredByVaiu: true,
      accentBarClass: 'bg-yellow-400',
    };
  }

  if (host.includes('payformyfood.')) {
    return {
      brandKey: 'payformyfood',
      displayName: 'Pay For My Food',
      logoSrc: '/payformyfood-logo.png',
      showPoweredByVaiu: true,
      accentBarClass: 'bg-red-600',
    };
  }

  if (host.includes('atomicmarketplace.com')) {
    return {
      brandKey: 'atomic',
      displayName: 'Atomic Marketplace',
      logoSrc: VAIU_LOGO_SRC,
      showPoweredByVaiu: false,
      accentBarClass: 'bg-slate-800',
    };
  }

  if (host.includes('vaiupay.com')) {
    return {
      brandKey: 'vaiupay',
      displayName: 'VaiuPay',
      logoSrc: VAIU_LOGO_SRC,
      showPoweredByVaiu: false,
      accentBarClass: 'bg-slate-800',
    };
  }

  return {
    brandKey: 'vaiu',
    displayName: 'Vaiu',
    logoSrc: VAIU_LOGO_SRC,
    showPoweredByVaiu: false,
    accentBarClass: 'bg-slate-900',
  };
}

/** True when hostname is the core consumer app (vaiu.app), not a bank subdomain. */
export function isCoreVaiuAppHost(hostname: string = window.location.hostname): boolean {
  const host = hostname.toLowerCase();
  if (!host.includes('vaiu.app')) return false;
  return !isBankTenantBrand(resolveTenantBranding(host).brandKey);
}
